import { isApiError } from "@/services/BaseService";

export interface AttachmentUploadErrorDetails {
    message: string;
    statusCode?: number;
    isDuplicate: boolean;
    isPayloadTooLarge: boolean;
    isUnsupportedType: boolean;
    duplicateFiles: string[];
}

type UploadEntity = "request" | "trip";

interface DuplicateErrorShape {
    isDuplicate?: boolean;
    fileName?: string;
    errorMessage?: string;
}

const parseJson = (value?: string | null): unknown | null => {
    if (!value) {
        return null;
    }

    try {
        return JSON.parse(value);
    } catch {
        return null;
    }
};

const getString = (value: unknown): string | null => {
    return typeof value === "string" && value.trim() ? value : null;
};

const getDuplicateFilesFromPayload = (payload: unknown): string[] => {
    if (!payload || typeof payload !== "object") {
        return [];
    }

    const data = payload as {
        fileName?: string;
        errors?: DuplicateErrorShape[];
    };

    const directFile = getString(data.fileName);
    const filesFromErrors = Array.isArray(data.errors)
        ? data.errors
              .filter((error) => error.isDuplicate)
              .map((error) => getString(error.fileName))
              .filter((name): name is string => Boolean(name))
        : [];

    return [...(directFile ? [directFile] : []), ...filesFromErrors];
};

const hasDuplicateHintInPayload = (payload: unknown): boolean => {
    if (!payload || typeof payload !== "object") {
        return false;
    }

    const data = payload as {
        isDuplicate?: boolean;
        duplicateCount?: number;
        errors?: DuplicateErrorShape[];
    };

    return (
        Boolean(data.isDuplicate) ||
        (typeof data.duplicateCount === "number" && data.duplicateCount > 0) ||
        (Array.isArray(data.errors) &&
            data.errors.some((error) => Boolean(error.isDuplicate)))
    );
};

const hasDuplicateHintInText = (message: string): boolean =>
    /already exists|duplicate/i.test(message);

const getEntityLabel = (entity: UploadEntity): "request" | "trip" => entity;

const getPayloadMessage = (payload: unknown): string | null => {
    if (!payload || typeof payload !== "object") {
        return null;
    }

    const data = payload as {
        message?: unknown;
        error?: unknown;
        errorMessage?: unknown;
        errors?: Array<{ errorMessage?: unknown; message?: unknown }>;
    };

    const directMessage =
        getString(data.message) ||
        getString(data.error) ||
        getString(data.errorMessage);
    if (directMessage) {
        return directMessage;
    }

    if (Array.isArray(data.errors) && data.errors.length > 0) {
        const firstError = data.errors[0];
        return (
            getString(firstError?.errorMessage) ||
            getString(firstError?.message)
        );
    }

    return null;
};

export const mapAttachmentUploadError = (
    error: unknown,
    entity: UploadEntity
): AttachmentUploadErrorDetails => {
    const fallbackMessage = `Failed to upload attachment(s) to the ${getEntityLabel(entity)}.`;

    let rawMessage = fallbackMessage;
    let statusCode: number | undefined;
    let payload: unknown | null = null;

    if (isApiError(error)) {
        rawMessage = error.message || fallbackMessage;
        statusCode = error.status;
        payload = parseJson(error.bodyText) ?? parseJson(error.message);
    } else if (error instanceof Error) {
        rawMessage = error.message || fallbackMessage;
        payload = parseJson(error.message);
    } else if (typeof error === "string" && error.trim()) {
        rawMessage = error;
        payload = parseJson(error);
    }

    const payloadMessage = getPayloadMessage(payload);
    const normalizedMessage = payloadMessage || rawMessage;
    const duplicateFiles = getDuplicateFilesFromPayload(payload);
    const isDuplicate =
        hasDuplicateHintInPayload(payload) ||
        hasDuplicateHintInText(normalizedMessage);
    const isPayloadTooLarge = statusCode === 413;
    const isUnsupportedType = statusCode === 415;

    if (isPayloadTooLarge) {
        return {
            message:
                "Attachment upload payload is too large for the server. Please reduce files per upload attempt and keep total attachments within 60MB.",
            statusCode,
            isDuplicate,
            isPayloadTooLarge: true,
            isUnsupportedType: false,
            duplicateFiles,
        };
    }

    if (isUnsupportedType) {
        return {
            message:
                "One or more files use an unsupported format. Please upload only allowed attachment types.",
            statusCode,
            isDuplicate,
            isPayloadTooLarge: false,
            isUnsupportedType: true,
            duplicateFiles,
        };
    }

    if (isDuplicate) {
        if (duplicateFiles.length === 1) {
            return {
                message: `The file "${duplicateFiles[0]}" already exists in this ${getEntityLabel(entity)}.`,
                statusCode,
                isDuplicate: true,
                isPayloadTooLarge: false,
                isUnsupportedType: false,
                duplicateFiles,
            };
        }

        if (duplicateFiles.length > 1) {
            return {
                message: `The following files already exist in this ${getEntityLabel(entity)}: ${duplicateFiles.join(", ")}.`,
                statusCode,
                isDuplicate: true,
                isPayloadTooLarge: false,
                isUnsupportedType: false,
                duplicateFiles,
            };
        }

        return {
            message: `One or more files already exist in this ${getEntityLabel(entity)}.`,
            statusCode,
            isDuplicate: true,
            isPayloadTooLarge: false,
            isUnsupportedType: false,
            duplicateFiles,
        };
    }

    if (statusCode === 400 && payloadMessage) {
        return {
            message: payloadMessage,
            statusCode,
            isDuplicate: false,
            isPayloadTooLarge: false,
            isUnsupportedType: false,
            duplicateFiles,
        };
    }

    return {
        message: normalizedMessage || fallbackMessage,
        statusCode,
        isDuplicate: false,
        isPayloadTooLarge: false,
        isUnsupportedType: false,
        duplicateFiles,
    };
};
